import { useEffect } from 'react';
import { z } from 'zod';
import { useCookieState, useLatest } from 'ahooks';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useAppData } from '~/modules/root';

const MESSAGE_SCHEMA = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('chat/message'),
        conversation_id: z.number(),
        sender_user_id: z.number(),
    }),
    z.object({
        type: z.literal('chat/update'),
        conversation_id: z.number(),
    }),
]);

type MessageType = z.infer<typeof MESSAGE_SCHEMA>;

/**
 * Hook to handle real-time chat with the WebSocket server.
 */
export function useRealTimeChat({
    onMessage,
}: {
    onMessage?: (message: MessageType) => void;
} = {}) {
    const [token] = useCookieState('__ap_tkn');
    const webSocketUrl = useAppData().webSocketUrl;
    const { lastJsonMessage, readyState } = useWebSocket<MessageType>(
        webSocketUrl,
        {
            queryParams: token ? { token } : undefined,
            share: true,
            heartbeat: {
                timeout: 10_000,
                interval: 60_000,
            },
            filter: (message) => {
                if (isHeartbeatMessage(message.data)) {
                    return false;
                }
                if (typeof message.data !== 'string') {
                    return false;
                }
                try {
                    MESSAGE_SCHEMA.parse(JSON.parse(message.data));
                    return true;
                } catch (error) {
                    return false;
                }
            },
            shouldReconnect: () => !!token,
            reconnectAttempts: 20,
            reconnectInterval: (lastAttemptNumber) => {
                if (lastAttemptNumber <= 2) {
                    // First 3 attempts.
                    return 2_000; // 2 seconds
                }
                // Use exponential backoff for reconnect attempts after the 3rd attempt.
                const baseDelay = 30_000; // 30 seconds
                const maxDelay = 3_600_000; // 1 hour
                const exponent = lastAttemptNumber - 3;
                return Math.min(baseDelay * Math.pow(2, exponent), maxDelay);
            },
        }
    );
    const latestOnMessage = useLatest(onMessage);

    useEffect(() => {
        if (lastJsonMessage && latestOnMessage.current) {
            latestOnMessage.current(lastJsonMessage);
        }
    }, [lastJsonMessage, latestOnMessage]);

    return {
        lastMessage: lastJsonMessage,
        isConnected: readyState === ReadyState.OPEN,
    };
}

/**
 * Check if a message from the WebSocket is a heartbeat message.
 * @param message The message to check.
 * @returns The message is a heartbeat message.
 */
function isHeartbeatMessage(message: unknown) {
    return !message || message === 'ping' || message === 'pong';
}
